import { Injectable } from '@angular/core';
import * as $ from 'jquery';
import { asEnumerable } from 'linq-es2015';
import { Subject } from 'rxjs';
import { DateHelperService } from '../../shared/helpers/date-helper.service';
import { globalUtilsHelper } from '../../shared/helpers/global-utils-helper';
import { DynamicRenderizerComponentService } from '../../shared/services/dynamic-renderizer-component.service';
import { LayerTooltipComponent } from '../components/layer-tooltip/layer-tooltip.component';
import { Leaflet } from '../leaflet-export';
import { SelectedLayerInfo } from '../models/selected-layer-info';

@Injectable()
export class LeafletBetterWmsService {
  id: string;
  isInitialized = false;

  constructor(
    private _dynamicRenderizerService: DynamicRenderizerComponentService,
    private _dateHelperService: DateHelperService
  ) {
    this.id = globalUtilsHelper.generateGuid();
  }

  layersToLoad = [];
  betterWmsLayers = [];
  clickedLayers = {};

  private _selectedLayer$ = new Subject<SelectedLayerInfo>();
  readonly selectedLayer$ = this._selectedLayer$.asObservable();

  extends() {
    if (this.isInitialized) {
      return;
    }
    this.isInitialized = true;

    const that = this;

    Leaflet.TileLayer.BetterWMS = Leaflet.TileLayer.WMS.extend({
      onAdd: function (map) {
        // Triggered when the layer is added to a map.
        //   Register a click listener, then do all the upstream WMS things
        Leaflet.TileLayer.WMS.prototype.onAdd.call(this, map);
        map.on('click', this.getFeatureInfo, this);
        that.betterWmsLayers.push(this);
      },

      onRemove: function (map) {
        // Triggered when the layer is removed from a map.
        //   Unregister a click listener, then do all the upstream WMS things
        Leaflet.TileLayer.WMS.prototype.onRemove.call(this, map);
        map.off('click', this.getFeatureInfo, this);
        that.betterWmsLayers = that.betterWmsLayers?.filter((x) => x !== this);
      },

      getFeatureInfo: function (evt) {
        const map = this['_map'];
        // Make an AJAX request to the server and hope for the best

        var url = this.getFeatureInfoUrl(evt.latlng);

        const pane = this.options.pane;
        const domPane = map.getPane(pane);
        const zIndex = domPane.style.zIndex;
        that.layersToLoad.push({ name: pane, zIndex, mapId: this.options.mapId });

        $.ajax({
          url: url,
          success: function (data, status, xhr) {
            var err = typeof data === 'string' ? null : data;
            that.clickedLayers[pane] = err;
            that.checkIfAllCliked(evt.latlng, map);
          },
          error: function (xhr, status, error) {
            that.clickedLayers[pane] = null;
            that.checkIfAllCliked(evt.latlng, map);
          },
        });
      },

      getFeatureInfoUrl: function (latlng) {
        // Construct a GetFeatureInfo request URL given a point

        const point = this._map.latLngToContainerPoint(latlng, this._map.getZoom());
        const size = this._map.getSize();
        const currentTime = this._map.waterTimeBar.getCurrentStep();
        const time = that._dateHelperService.toApiFormat(new Date(currentTime));
        const height = Math.round(size.y);
        const width = Math.round(size.x);

        const params = {
          request: 'GetFeatureInfo',
          service: 'WMS',
          srs: 'EPSG:4326',
          time: time,
          styles: this.wmsParams.styles,
          transparent: this.wmsParams.transparent,
          version: this.wmsParams.version,
          format: this.wmsParams.format,
          bbox: this._map.getBounds().toBBoxString(),
          height,
          width,
          layers: this.wmsParams.layers,
          query_layers: this.wmsParams.layers,
          info_format: 'application/json',
        };

        const pointX = Math.round(point.x);
        const pointY = Math.round(point.y);

        params[params.version === '1.3.0' ? 'i' : 'x'] = pointX;
        params[params.version === '1.3.0' ? 'j' : 'y'] = pointY;

        // return this._url + Leaflet.Util.getParamString(params, this._url, true);

        var url = this._url + Leaflet.Util.getParamString(params, this._url, true);

        /**
         * CORS workaround (using a basic php proxy)
         *
         * Added 2 new options:
         *  - proxy
         *  - proxyParamName
         *
         */

        // check if "proxy" option is defined (PS: path and file name)
        if (typeof this.wmsParams.proxy !== 'undefined') {
          // check if proxyParamName is defined (instead, use default value)
          if (typeof this.wmsParams.proxyParamName !== 'undefined')
            this.wmsParams.proxyParamName = 'url';

          // build proxy (es: "proxy.php?url=" )
          const _proxy = this.wmsParams.proxy + '?' + this.wmsParams.proxyParamName + '=';

          url = _proxy + encodeURIComponent(url);
        }

        return url;
      },
    });

    Leaflet.tileLayer.betterWms = function (url, options) {
      return new Leaflet.TileLayer.BetterWMS(url, options);
    };
  }

  private addPopup(properties, metadata) {
    const layerTooltipValues = JSON.parse(properties.values);

    const popup = this._dynamicRenderizerService.injectComponentWithoutDestroyIt(
      LayerTooltipComponent,
      'div',
      (cmp, cmpRef) => {
        cmp.title = properties.name;
        cmp.subtitle = properties.neType;
        cmp.metadata = metadata;
        cmp.metadataKey = `${properties.snapshotId}-${properties.scenarioId}`;
        cmp.tooltipPropertyValues = layerTooltipValues;
        cmp.elementType = properties.neType;
      }
    );

    return popup;
  }

  setLayerClicked(layer) {
    this._selectedLayer$.next(layer);
  }

  checkIfAllCliked(latlng, map) {
    const layersOrderByZIndex = asEnumerable(this.layersToLoad)
      .Where((x) => x.mapId == map.options.mapId)
      .OrderByDescending((x) => x.zIndex)
      .ToArray();

    const currentMapLayers = this.betterWmsLayers.filter(
      (x) => x.options.mapId == map.options.mapId
    );
    if (currentMapLayers.length === Object.keys(this.clickedLayers).length) {
      let selectedLayer = null;
      for (const layer of layersOrderByZIndex) {
        const data = this.clickedLayers[layer.name];

        if (data?.features?.length) {
          selectedLayer = data;

          this.finalGetFeatureInfo(selectedLayer, latlng, map);
          break;
        }
      }

      this.clickedLayers = {};
      this.layersToLoad = [];
    }
  }

  finalGetFeatureInfo(data, latlng, map): void {
    this.showGetFeatureInfo(data, latlng, map);
  }

  showGetFeatureInfo(content, latlng, map) {
    if (!content?.features?.length) {
      return;
    }

    const layerProperties = this.getLayerProperties(content);

    const metadata = map.options.metadata;

    if (!layerProperties) {
      return;
    }

    // Otherwise show the content in a popup, or something.
    const popup = this.addPopup(layerProperties, metadata);

    //send
    const step = map.waterTimeBar.getCurrentStep();
    const layerId = layerProperties.neId;
    const layerType = layerProperties.neType;
    const scenarioId = layerProperties.scenarioId;
    const snapshotId = layerProperties.snapshotId ?? layerProperties.simulationId;

    const selectedLayer = new SelectedLayerInfo({
      layerId,
      step: new Date(step),
      layerType,
      scenarioId,
      snapshotId,
      elementId: layerProperties.neId,
      latLng: latlng,
      mapId: map.options.mapId,
    });

    this.setLayerClicked(selectedLayer);

    Leaflet.popup({ maxWidth: 800 }).setLatLng(latlng).setContent(popup).openOn(map);
  }

  private getLayerProperties = (content) =>
    asEnumerable(content.features).FirstOrDefault()['properties'];
}
